import React, { createContext, useCallback, useContext, useState, useMemo } from "react";
import { ChildrenType, UserCookieType } from "../helper/types";
import { getCookie, setCookie } from "react-use-cookie";

export const UserCookieName = 'user';
export const ParentUserCookieName = 'parentUser';
export const ParentUserOrgCookieName = 'parentOrgDetail';

export type ParentUserOrgType = {
  oId: string,
  oname: string
}
/*
  * User Context Type
*/
type UserContextType = {
  isAuthenticated: boolean;
  user: UserCookieType | null;
  parentUser: UserCookieType | null;
  parentUserOrg: ParentUserOrgType | null;
  userCookieData: UserCookieType | undefined;
  parentUserCookieData: UserCookieType | undefined;
  parentUserOrgCookieData: ParentUserOrgType | undefined;
  setUser: (data: UserCookieType) => void;
  setParentUser: (data: UserCookieType) => void;
  setParentUserOrg: (data: ParentUserOrgType) => void;
  removeUser: () => void,
  isSuperAdmin: boolean
}

/*
  * User Context Default Value
*/
const userDefaultValues: UserContextType = {
  isAuthenticated: false,
  user: null,
  parentUser: null,
  parentUserOrg: null,
  userCookieData: undefined,
  parentUserCookieData: undefined,
  parentUserOrgCookieData: undefined,
  setUser: (data: UserCookieType) => { },
  setParentUser: (data: UserCookieType) => { },
  setParentUserOrg: (data: ParentUserOrgType) => { },
  removeUser: () => { },
  isSuperAdmin: false
};

/*
  * User Context
*/
const UserContext = createContext<UserContextType>(userDefaultValues);

export const useUser = () => useContext(UserContext);

/*
  * User Provider
*/
const UserProvider: React.FC<ChildrenType> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<UserCookieType | null>(null);
  const [parentUser, setParentUser] = useState<UserCookieType | null>(null);
  const [parentUserOrg, setParentUserOrg] = useState<ParentUserOrgType | null>(null);

  /*
   * Function for setting user state
   */
  const authenticatedUserHandler = useCallback((data: UserCookieType) => {
    setCookie(UserCookieName, btoa(JSON.stringify(data)));
    setUser(data);
    setIsAuthenticated(true);
  }, []);

  const parentUserHandler = useCallback((data: UserCookieType) => {
    setCookie(ParentUserCookieName, btoa(JSON.stringify(data)));
    setParentUser(data);
  }, []);

  const parentUserOrgHandler = useCallback((data: ParentUserOrgType) => {
    setCookie(ParentUserOrgCookieName, btoa(JSON.stringify(data)));
    setParentUserOrg(data);
  }, []);

  const removeUser = useCallback(() => {
    setCookie(UserCookieName, "");
    setCookie(ParentUserOrgCookieName, "")
    setCookie(ParentUserCookieName, "");
    setUser(null);
    setParentUser(null);
    setParentUserOrg(null);
    setIsAuthenticated(false);
  }, []);

  const fetchCookie = useCallback((value: string) => getCookie(value), [])

  const userCookieData = useMemo(() => {
    const cookie = fetchCookie(UserCookieName);
    if (cookie.length > 0) {
      try {
        let encryptedString = atob(cookie);
        let decryptedData = JSON.parse(encryptedString) as UserCookieType | string | null;
        if (decryptedData && typeof decryptedData !== 'string') {
          return decryptedData as UserCookieType;
        } else {
          return undefined;
        }
      } catch (error) {
        return undefined;
      }
    }
    return undefined;
  }, [fetchCookie]);

  const parentUserCookieData = useMemo(() => {
    const cookie = fetchCookie(ParentUserCookieName);
    if (cookie.length > 0) {
      try {
        let encryptedString = atob(cookie);
        let decryptedData = JSON.parse(encryptedString) as UserCookieType | string | null;
        if (decryptedData && typeof decryptedData !== 'string') {
          return decryptedData as UserCookieType;
        } else {
          return undefined;
        }
      } catch (error) {
        return undefined;
      }
    }
    return undefined;
  }, [fetchCookie]);

  const isSuperAdmin = useMemo(() => Boolean(user?.user.profile.roles === "SUPER_ADMIN"), [user?.user.profile.roles])

  const parentUserOrgCookieData = useMemo(() => {
    const cookie = fetchCookie(ParentUserOrgCookieName);
    if (cookie.length > 0) {
      try {
        let encryptedString = atob(cookie);
        let decryptedData = JSON.parse(encryptedString) as {
          oId: string,
          oname: string
        } | string | null;
        if (decryptedData && typeof decryptedData !== 'string') {
          return decryptedData as {
            oId: string,
            oname: string
          };
        } else {
          return undefined;
        }
      } catch (error) {
        return undefined;
      }
    }
    return undefined;
  }, [fetchCookie]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser: authenticatedUserHandler,
        parentUser,
        setParentUser: parentUserHandler,
        parentUserOrg,
        setParentUserOrg: parentUserOrgHandler,
        isAuthenticated,
        removeUser,
        userCookieData,
        parentUserCookieData,
        parentUserOrgCookieData,
        isSuperAdmin
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export default UserProvider;